import axios from 'axios';
import logger from './logger';

const rawUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.fitxero.com';
const BACKEND_URL = rawUrl.replace(/\/+$/, ''); // Strip trailing slashes
export const API = `${BACKEND_URL}/api`;

export const setAuthToken = token => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('token', token);
  } else {
    delete axios.defaults.headers.common['Authorization'];
    localStorage.removeItem('token');
  }
};

export const getAuthToken = () => {
  return localStorage.getItem('token');
};

export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  if (!userStr || userStr === 'undefined') {
    return null;
  }
  try {
    return JSON.parse(userStr);
  } catch (e) {
    return null;
  }
};

export const setCurrentUser = user => {
  if (user === undefined) {
    localStorage.removeItem('user');
  } else {
    localStorage.setItem('user', JSON.stringify(user));
  }
};

export const getGymData = () => {
  const gymStr = localStorage.getItem('gym');
  if (!gymStr || gymStr === 'undefined') {
    return null;
  }
  try {
    return JSON.parse(gymStr);
  } catch (e) {
    return null;
  }
};

export const setGymData = gym => {
  if (gym === undefined) {
    localStorage.removeItem('gym');
  } else {
    localStorage.setItem('gym', JSON.stringify(gym));
  }
};

export const clearAuth = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('gym');
  delete axios.defaults.headers.common['Authorization'];
};

const token = getAuthToken();
if (token) {
  setAuthToken(token);
}

// ─── Secure Interceptors ─────────────────────────────────────────────────────
// Logs are sanitized — no PII, tokens, or full payloads leak to the console.
axios.interceptors.request.use(
  config => {
    const isEmailRelated = /reminder|announcement|send|test-email/i.test(config.url || '');
    if (isEmailRelated) {
      logger.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }
    return config;
  },
  error => {
    logger.error('API request failed', {
      url: error.config?.url,
      method: error.config?.method,
    });
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  response => {
    const isEmailRelated = /reminder|announcement|send|test-email/i.test(
      response.config?.url || ''
    );
    if (isEmailRelated) {
      logger.debug(`API Response: ${response.status} ${response.config?.url}`);
    }
    return response;
  },
  error => {
    const url = error.config?.url || 'unknown';

    // Automatically intercept backend subscription expiration (402 Payment Required)
    if (error.response?.status === 402) {
      const gymData = getGymData();
      if (gymData) {
        if (gymData.profile) {
          gymData.profile.planStatus = 'EXPIRED';
        } else {
          gymData.planStatus = 'EXPIRED';
        }
        setGymData(gymData);
        // Force the app to re-read localStorage and PlanContext to re-render the lock screen globally
        window.dispatchEvent(new Event('storage'));
        window.location.reload();
      }
    }

    // Automatically intercept 401 Unauthorized (expired/invalid token)
    if (error.response?.status === 401) {
      clearAuth();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    const isEmailRelated = /reminder|announcement|send|test-email/i.test(url);
    if (isEmailRelated) {
      logger.error(`API Error: ${error.response?.status || 'NETWORK'} ${url}`);
    }
    return Promise.reject(error);
  }
);
